import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { Auth } from '@common/auth/models/auth.model';
import { switchMapKeep } from '@common/util/switch-map-keep.operator';
import { environment } from '@env/environment';
import { AuthService } from '@portal-core/auth/services/auth.service';
import { ApiService } from '@portal-core/auth/services/api.service';
import { BrowserHostService } from '@portal-core/auth/services/browser-host.service';
import { from, map, Observable, of, switchMap, tap } from 'rxjs';

/**
 * Emits a login event for Flare once the user is logged in with an active license user on an active license.
 */
export const flareLoginEmitterResolver: ResolveFn<boolean> = (): Observable<boolean> => {
  const browserHostService = inject(BrowserHostService);
  const apiService = inject(ApiService);
  const authService = inject(AuthService);

  // If the browser host is defined then run the code to emit the login event
  if (browserHostService.isCefSharpHosted()) {
    return browserHostService.browserHost$().pipe(
      switchMapKeep(browserHost => from(browserHost.version())),
      switchMap<[BrowserHost, number], Observable<[BrowserHost, Auth]>>(([browserHost, browserHostVersion]) => {
        if (browserHostVersion < 3) {
          // For Flare 2025 r2 and earlier, auth data is expected to be emitted so we need to grab it from the licenses/login endpoint
          return authService.loginToLicense$().pipe(map(auth => [browserHost, auth]));
        } else {
          // After Flare 2025 r2, auth data is grabbed from the browser host cookies by the API url
          return of([browserHost, {
            'as:client_id': environment.centralApiClientId,
            host: new URL(apiService.centralApiBaseUri).host,
          } as Auth]);
        }
      }),
      tap(([browserHost, auth]) => {
        // Emit the login event
        browserHost?.data?.({
          type: 'login',
          auth
        });
      }),
      map(() => true)
    );
  }

  return of(true);
};
